import React, { useEffect, useState } from "react";
import Headers from "../components/Headers";
import Footer from "../components/Footer";
import { Link } from "react-router-dom";
import { MdOutlineKeyboardArrowRight } from "react-icons/md";
import { FiMail, FiPhone, FiMapPin } from "react-icons/fi";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import { useDispatch, useSelector } from "react-redux";
import { save_contact, messageClear } from "../store/reducers/contactReducer";
import { get_banners } from "../store/reducers/homeReducer";
import toast from "react-hot-toast";
import Carousel from "react-multi-carousel";
import "react-multi-carousel/lib/styles.css";
import { MdLocationOn } from "react-icons/md";
import { IoIosCall } from "react-icons/io";
import { GrMail } from "react-icons/gr";

const schema = yup.object().shape({
  name: yup.string().required("Name is required"),
  email: yup.string().email("Invalid email").required("Email is required"),
  subject: yup.string().required("Subject is required"),
  message: yup.string().required("Message is required"),
});

const Contact = ({ sellerId, sellerName }) => {
  const dispatch = useDispatch();
  const { loader, errorMessage, successMessage } = useSelector((state) => state.contact);
  const { supportInfo, seller } = useSelector((state) => state.auth);
  const { banners } = useSelector((state) => state.home);
  useEffect(() => {
    document.title = "4POS eCommerce Customer";
  }, []);
  const responsiveBanner = {
    superLargeDesktop: { breakpoint: { max: 4000, min: 3000 }, items: 1 },
    desktop: { breakpoint: { max: 3000, min: 1024 }, items: 1 },
    tablet: { breakpoint: { max: 1024, min: 464 }, items: 1 },
    mobile: { breakpoint: { max: 464, min: 0 }, items: 1 },
  };
  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm({
    resolver: yupResolver(schema),
  });
  useEffect(() => {
    dispatch(get_banners(sellerId));
  }, [dispatch, sellerId]);
  useEffect(() => {
    if (successMessage) {
      toast.success(successMessage);
      reset();
      dispatch(messageClear());
    }
    if (errorMessage) {
      toast.error(errorMessage);
      dispatch(messageClear());
    }
  }, [successMessage, errorMessage, dispatch, reset]);
  if (!sellerName) {
    console.warn('Contact: sellerName is undefined');
    return null;
  }

  const submitForm = (data) => {
    dispatch(save_contact({ ...data, sellerId }));
  };

  return (
    <>
      <Headers sellerId={sellerId} sellerName={sellerName} />

      <section className="h-[200px] mt-6 bg-cover bg-no-repeat relative bg-left">
        <Carousel
          autoPlay={true}
          infinite={true}
          arrows={false}
          showDots={false}
          responsive={responsiveBanner}
          className="h-full"
        >
          {banners &&
            banners.length > 0 &&
            banners.map((b, i) => (
              <div key={i} className="h-full w-full">
                <img
                  className="w-full h-full object-cover"
                  src={b.banner}
                  alt=""
                />
              </div>
            ))}
        </Carousel>
        <div className="absolute left-0 top-0 w-full h-full bg-black/40 backdrop-blur-[2px]">
          <div className="w-[85%] md:w-[80%] sm:w-[90%] lg:w-[90%] h-full mx-auto">
            <div className="flex flex-col justify-center gap-2 items-center h-full w-full text-white">
              <h2 className="text-3xl font-semibold">Contact Us</h2>
              <div className="flex justify-center items-center gap-2 text-sm">
                <Link to={`/${sellerName}`} className="hover:text-indigo-300 transition-colors duration-300">Home</Link>
                <span className="pt-1">
                  <MdOutlineKeyboardArrowRight />
                </span>
                <span>Contact</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="py-16">
        <div className="w-[85%] md:w-[90%] sm:w-[90%] lg:w-[90%] mx-auto">
          <div className="grid grid-cols-2 md-lg:grid-cols-1 gap-8">
            {/* Contact Information */}
            <div className="bg-white p-8 rounded-xl shadow-sm">
              <h2 className="text-2xl font-semibold text-gray-800 mb-6">Contact Information</h2>
              <div className="space-y-6">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 rounded-full bg-indigo-50 flex items-center justify-center flex-shrink-0">
                    <MdLocationOn className="text-2xl text-indigo-600" />
                  </div>
                  <div>
                    <h3 className="text-lg font-medium text-gray-800 mb-1">Our Location</h3>
                    <p className="text-gray-600">{seller?.support?.address || "Address not provided"}</p>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 rounded-full bg-indigo-50 flex items-center justify-center flex-shrink-0">
                    <IoIosCall className="text-2xl text-indigo-600" />
                  </div>
                  <div>
                    <h3 className="text-lg font-medium text-gray-800 mb-1">Phone Number</h3>
                    <p className="text-gray-600">{seller?.support?.mobileNo || "Phone number not provided"}</p>
                    {seller?.support?.supportHours && (
                      <p className="text-sm text-gray-500 mt-1">Support Hours: {seller.support.supportHours}</p>
                    )}
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 rounded-full bg-indigo-50 flex items-center justify-center flex-shrink-0">
                    <GrMail className="text-2xl text-indigo-600" />
                  </div>
                  <div>
                    <h3 className="text-lg font-medium text-gray-800 mb-1">Email Address</h3>
                    <p className="text-gray-600">{seller?.support?.email || "Email not provided"}</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Contact Form */}
            <div className="bg-white p-8 rounded-xl shadow-sm">
              <h2 className="text-2xl font-semibold text-gray-800 mb-6">Send us a Message</h2>
              <form onSubmit={handleSubmit(submitForm)} className="space-y-6">
                <div className="grid grid-cols-2 gap-4">
                  <div className="flex flex-col gap-1">
                    <label htmlFor="name" className="text-sm font-medium text-gray-700">Your Name</label>
                    <input
                      {...register("name")}
                      className="w-full px-4 py-2.5 bg-gray-50 border border-gray-200 outline-none focus:border-indigo-500 focus:bg-white rounded-lg transition-colors duration-200"
                      placeholder="John Doe"
                      id="name"
                    />
                    {errors.name && (
                      <p className="text-red-500 text-xs mt-1">
                        {errors.name.message}
                      </p>
                    )}
                  </div>

                  <div className="flex flex-col gap-1">
                    <label htmlFor="email" className="text-sm font-medium text-gray-700">Your Email</label>
                    <input
                      {...register("email")}
                      className="w-full px-4 py-2.5 bg-gray-50 border border-gray-200 outline-none focus:border-indigo-500 focus:bg-white rounded-lg transition-colors duration-200"
                      placeholder="john@example.com"
                      id="email"
                    />
                    {errors.email && (
                      <p className="text-red-500 text-xs mt-1">
                        {errors.email.message}
                      </p>
                    )}
                  </div>
                </div>

                <div className="flex flex-col gap-1">
                  <label htmlFor="subject" className="text-sm font-medium text-gray-700">Subject</label>
                  <input
                    {...register("subject")}
                    className="w-full px-4 py-2.5 bg-gray-50 border border-gray-200 outline-none focus:border-indigo-500 focus:bg-white rounded-lg transition-colors duration-200"
                    placeholder="How can we help you?"
                    id="subject"
                  />
                  {errors.subject && (
                    <p className="text-red-500 text-xs mt-1">
                      {errors.subject.message}
                    </p>
                  )}
                </div>

                <div className="flex flex-col gap-1">
                  <label htmlFor="message" className="text-sm font-medium text-gray-700">Message</label>
                  <textarea
                    {...register("message")}
                    className="w-full px-4 py-2.5 bg-gray-50 border border-gray-200 outline-none focus:border-indigo-500 focus:bg-white rounded-lg transition-colors duration-200 h-32 resize-none"
                    placeholder="Your message"
                    id="message"
                  ></textarea>
                  {errors.message && (
                    <p className="text-red-500 text-xs mt-1">
                      {errors.message.message}
                    </p>
                  )}
                </div>

                <button
                  disabled={loader}
                  className={`px-8 py-2.5 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors duration-200 font-medium text-sm ${
                    loader ? "opacity-50 cursor-not-allowed" : ""
                  }`}
                >
                  {loader ? "Sending..." : "Send Message"}
                </button>
              </form>
            </div>
          </div>
        </div>
      </section>

      <Footer sellerId={sellerId} sellerName={sellerName} />
    </>
  );
};

export default Contact;
